/**
 * BigSplice Sheets Service
 *
 * Communicates with the bigsplice-purerust-orchestrator Lambda
 * for high-performance parquet data operations.
 *
 * Search results are cached as parquet files in S3 for fast retrieval.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { lambdaEndpoints } from '../aws-config';

// API Gateway endpoint for the purerust orchestrator
// Used for all operations (data loading, search, distinct, etc.)
const ORCHESTRATOR_URL = lambdaEndpoints.sheets;

export interface SchemaColumn {
  name: string;
  data_type: string;
  nullable: boolean;
}

export interface SchemaResponse {
  success: boolean;
  row_count?: number;
  row_group_count?: number;
  schema?: SchemaColumn[];
  bytes_read?: number;
  file_size?: number;
  message?: string;
  error?: string;
}

export interface SearchResponse {
  success: boolean;
  data?: Record<string, any>[];
  matched_rows?: number;
  total_matched_before_limit?: number;
  rows_scanned?: number;
  row_groups_scanned?: number;
  row_groups_skipped?: number;
  bytes_read?: number;
  workers_used?: number;
  num_row_groups?: number;
  cache_hit?: boolean;
  // New cache-based fields
  cache_key?: string;
  cache_bucket?: string;
  message?: string;
  error?: string;
}

export interface DistinctResponse {
  success: boolean;
  column?: string;
  distinct_values?: (string | null)[];
  total_unique?: number;
  bytes_read?: number;
  message?: string;
  error?: string;
}

export interface CountResponse {
  success: boolean;
  row_count?: number;
  row_group_count?: number;
  row_group_sizes?: number[];
  message?: string;
  error?: string;
}

export interface SearchCriteria {
  column: string;
  operator: 'eq' | 'ne' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'starts_with' | 'ends_with';
  value: string;
}

class SheetsService {
  private async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  private async invokeLambda(payload: Record<string, any>, url: string = ORCHESTRATOR_URL): Promise<any> {
    const token = await this.getAuthToken();

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API Gateway error: ${response.status}`);
      }

      // Handle gzip-compressed responses
      const contentEncoding = response.headers.get('Content-Encoding');
      if (contentEncoding === 'gzip') {
        // Browser automatically decompresses gzip responses
        return await response.json();
      }

      return await response.json();
    } catch (error) {
      console.error('Sheets API error:', error);
      throw error;
    }
  }

  /**
   * Get schema (column names and types) for a parquet file or multipart dataset
   */
  async getSchema(bucket: string, key: string, options?: { isMultipart?: boolean }): Promise<SchemaResponse> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'schema',
      ...(options?.isMultipart ? { isMultipart: true } : {}),
    });
  }

  /**
   * Get row count and row group information
   */
  async getCount(bucket: string, key: string): Promise<CountResponse> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'count',
    });
  }

  /**
   * Search/filter data with criteria
   * Uses dedicated search orchestrator for fast parallel search
   * Returns data directly (also cached in S3 for future use)
   */
  async search(
    bucket: string,
    key: string,
    search: SearchCriteria,
    options: {
      select_columns?: string[];
      limit?: number;
      offset?: number;
      num_workers?: number;
      isMultipart?: boolean;
    } = {}
  ): Promise<SearchResponse> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'search',
      search,
      select_columns: options.select_columns,
      limit: options.limit || 1000,
      offset: options.offset || 0,
      num_workers: options.num_workers || 50,
      ...(options.isMultipart ? { isMultipart: true } : {}),
    }, ORCHESTRATOR_URL);
  }

  /**
   * Search and return results directly
   * Results are also cached in S3 for future use
   */
  async searchAndLoad(
    bucket: string,
    key: string,
    search: SearchCriteria,
    options: {
      select_columns?: string[];
      limit?: number;
      offset?: number;
      num_workers?: number;
      isMultipart?: boolean;
    } = {}
  ): Promise<SearchResponse> {
    // Search returns data directly (and caches in background)
    return this.search(bucket, key, search, options);
  }

  /**
   * Get all rows (match-all query with empty value)
   * Uses main orchestrator for data loading (NOT search orchestrator)
   */
  async getData(
    bucket: string,
    key: string,
    options: {
      select_columns?: string[];
      limit?: number;
      offset?: number;
      isMultipart?: boolean;
    } = {}
  ): Promise<SearchResponse> {
    // Get schema first to know which column to use
    const schemaResponse = await this.getSchema(bucket, key, { isMultipart: options.isMultipart });
    if (!schemaResponse.success || !schemaResponse.schema?.length) {
      return {
        success: false,
        error: 'Failed to get schema',
      };
    }

    const firstColumn = schemaResponse.schema[0].name;

    // Use main orchestrator for data loading (not search orchestrator)
    return this.invokeLambda({
      bucket,
      key,
      operation: 'search',
      search: {
        column: firstColumn,
        operator: 'contains',
        value: '', // Empty value = match all
      },
      select_columns: options.select_columns,
      limit: options.limit || 1000,
      offset: options.offset || 0,
      ...(options.isMultipart ? { isMultipart: true } : {}),
    }, ORCHESTRATOR_URL);
  }

  /**
   * Get distinct values for a column
   * Uses dedicated search orchestrator
   */
  async getDistinct(
    bucket: string,
    key: string,
    column: string,
    limit: number = 100,
    options?: { isMultipart?: boolean }
  ): Promise<DistinctResponse> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'distinct',
      column,
      limit,
      ...(options?.isMultipart ? { isMultipart: true } : {}),
    }, ORCHESTRATOR_URL);
  }

  /**
   * Search across multiple columns (OR logic)
   * Uses dedicated search orchestrator
   * Returns data directly (also cached in S3 for future use)
   */
  async searchAny(
    bucket: string,
    key: string,
    columns: string[],
    value: string,
    operator: string = 'contains',
    options: {
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<SearchResponse> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'search_any',
      columns,
      value,
      operator,
      limit: options.limit || 1000,
      offset: options.offset || 0,
    }, ORCHESTRATOR_URL);
  }

  /**
   * Search across multiple columns and return results directly
   * Results are also cached in S3 for future use
   */
  async searchAnyAndLoad(
    bucket: string,
    key: string,
    columns: string[],
    value: string,
    operator: string = 'contains',
    options: {
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<SearchResponse> {
    // Search returns data directly (and caches in background)
    return this.searchAny(bucket, key, columns, value, operator, options);
  }

  /**
   * Perform aggregation (GROUP BY)
   */
  async aggregate(
    bucket: string,
    key: string,
    options: {
      group_by?: string[];
      aggregations?: Array<{ column: string; function: 'count' | 'sum' | 'avg' | 'min' | 'max' }>;
      filters?: SearchCriteria;
      limit?: number;
    } = {}
  ): Promise<SearchResponse> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'aggregate',
      group_by: options.group_by || [],
      aggregations: options.aggregations || [{ column: '*', function: 'count' }],
      filters: options.filters,
      limit: options.limit || 1000,
    });
  }

  /**
   * Parse S3 key from file info
   */
  parseS3Key(fileInfo: { s3Key?: string; fileName?: string }): { bucket: string; key: string } {
    const key = fileInfo.s3Key || fileInfo.fileName || '';

    // Default bucket for BigSplice data
    const bucket = 'bigsplice-us-east-1';

    // If the key doesn't have the full path, assume it's in the user's data folder
    // The actual key format is: users/{userId}/data/{filename}
    // This will be properly resolved by the file list API

    return { bucket, key };
  }

  private get searchOrchestratorUrl(): string {
    return lambdaEndpoints.searchOrchestrator;
  }

  /**
   * Get a presigned download URL for a parquet file.
   * Used by the DuckDB WASM local mode to download files to the browser.
   */
  async getDownloadUrl(
    bucket: string,
    key: string,
    options?: { isMultipart?: boolean }
  ): Promise<{ success: boolean; url?: string; file_size?: number; error?: string }> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'download_url',
      ...(options?.isMultipart ? { isMultipart: true } : {}),
    });
  }

  /**
   * Search across all corpus types (pdf, audio, image) via lnx full-text search.
   */
  async corpusSearch(
    userId: string,
    query: string,
    limit: number = 50
  ): Promise<CorpusSearchResponse> {
    return this.invokeLambda({
      operation: 'corpus_search',
      user_id: userId,
      query,
      limit,
    }, this.searchOrchestratorUrl);
  }

  /**
   * Trigger building lnx indexes for corpus parquet files.
   */
  async buildCorpusIndexes(
    userId: string,
    corpusTypes?: string[]
  ): Promise<CorpusBuildResponse> {
    return this.invokeLambda({
      operation: 'build_corpus_index',
      user_id: userId,
      ...(corpusTypes ? { corpus_types: corpusTypes } : {}),
    }, this.searchOrchestratorUrl);
  }

  // AI Column Layers — stored in S3 alongside the data file

  async saveLayers(
    bucket: string,
    key: string,
    layers: Array<{ name: string; savedAt: string; columns: Array<{ name: string; prompt: string }>; cellData: Record<string, string> }>,
  ): Promise<{ success: boolean; error?: string }> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'save_layers',
      layers,
    });
  }

  async loadLayers(
    bucket: string,
    key: string,
  ): Promise<{ success: boolean; layers: Array<{ name: string; savedAt: string; columns: Array<{ name: string; prompt: string }>; cellData: Record<string, string> }>; error?: string }> {
    return this.invokeLambda({
      bucket,
      key,
      operation: 'load_layers',
    });
  }
}

// Corpus search types
export interface CorpusSearchResponse {
  status: string;
  results: Record<string, CorpusHit[]>;
  indexes_searched: number;
  total_time_ms: number;
  error?: string;
}

export interface CorpusHit {
  _corpus_type: string;
  _score: number;
  [key: string]: any;
}

export interface CorpusBuildResponse {
  status: string;
  built: string[];
  skipped: string[];
  errors: string[];
}

export const sheetsService = new SheetsService();
